import React, { type DragEvent } from 'react';
import { otherZone, type ZoneCoupling } from '../../../../types/coupling';
import { parseUtcTimestamp } from '../../../../utils/timeUtils';
import { COUPLING_CLOSED_COLOR, COUPLING_OPEN_COLOR } from '../../../shared/CouplingSwitchDialog';
import { CouplingFlowFigures } from '../../../shared/CouplingFlowFigures';
import { useCouplingStore } from '../../../../store/couplingStore';

interface CouplingRowProps {
  coupling: ZoneCoupling;
  activeZoneId: string;
  readOnly: boolean;
  onSwitch: (target: boolean) => void;
  /** KVS busbar view: rows can be dragged onto another busbar. */
  onDragStart?: (e: DragEvent<HTMLDivElement>, couplingId: string) => void;
}

const sourceLabel = (source: string) => {
  switch (source) {
    case 'import': return 'Import';
    case 'manual': return 'manuell';
    case 'measurement': return 'Messung';
    default: return source;
  }
};

/**
 * One Trennstelle as an "Abgang" row of a KVS busbar or an ONS: the coupling cable belongs to
 * the neighbour zone, so it is not in this zone's element list — without this row the busbar
 * view showed no trace of the outgoing cable that couples the two zones. Its loading, current
 * and the power crossing the Trennstelle come from the owner zone's result (`couplingStore.flows`),
 * so nobody has to switch zones to read them (Befund VER 2026-09-17).
 */
export const CouplingRow: React.FC<CouplingRowProps> = ({ coupling: c, activeZoneId, readOnly, onSwitch, onDragStart }) => {
  const neighbour = otherZone(c, activeZoneId);
  const weAreOwner = c.zoneAId === activeZoneId;
  const color = c.isClosed ? COUPLING_CLOSED_COLOR : COUPLING_OPEN_COLOR;
  const deviates = c.normalClosed != null && c.normalClosed !== c.isClosed;
  const changedAt = parseUtcTimestamp(c.stateChangedAt);
  const flow = useCouplingStore(s => s.flows[c.id]);

  return (
    <div
      className="flex items-start gap-1.5 px-1.5 py-1 rounded text-xs bg-white/60 dark:bg-primary-800/40 border border-dashed"
      style={{ borderColor: color }}
      draggable={!readOnly && !!onDragStart}
      onDragStart={onDragStart ? e => onDragStart(e, c.id) : undefined}
      title={`Trennstelle ${c.name} · ${c.isClosed ? 'geschlossen' : 'offen'} seit ${changedAt.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} (${sourceLabel(c.stateSource)}${c.stateChangedBy ? `, ${c.stateChangedBy}` : ''})`}
    >
      <span
        className="mt-0.5 inline-flex shrink-0 items-center justify-center w-4 h-4 rounded-full"
        style={{ backgroundColor: color }}
        aria-label={c.isClosed ? 'Trennstelle geschlossen' : 'Trennstelle offen'}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          {c.isClosed ? (
            <>
              <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
              <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
            </>
          ) : (
            <>
              <path d="M18.8 11.3a5 5 0 0 0-.7-6.6 5 5 0 0 0-7-.1l-1.7 1.7" />
              <path d="M5.2 12.7a5 5 0 0 0 .7 6.6 5 5 0 0 0 7 .1l1.7-1.7" />
              <line x1="2" y1="2" x2="22" y2="22" />
            </>
          )}
        </svg>
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-800 dark:text-gray-100 truncate">
          Trennstelle → {neighbour.name}
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
          {weAreOwner ? 'Kuppelkabel' : `Kuppelkabel aus ${neighbour.name}`}
          {c.cableName ? ` ${c.cableName}` : ''}
          {' · '}
          <span style={{ color }} className="font-semibold">{c.isClosed ? 'geschlossen' : 'offen'}</span>
          {deviates && <span className="text-red-600 dark:text-red-400 font-semibold"> · weicht vom Normalzustand ab</span>}
        </div>
        <CouplingFlowFigures coupling={c} flow={flow} activeZoneId={activeZoneId} variant="row" />
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onSwitch(!c.isClosed); }}
          className="shrink-0 px-2 py-0.5 rounded text-[10px] font-semibold text-white"
          style={{ backgroundColor: c.isClosed ? COUPLING_OPEN_COLOR : COUPLING_CLOSED_COLOR }}
        >
          {c.isClosed ? 'öffnen' : 'schließen'}
        </button>
      )}
    </div>
  );
};
